/**
 * Places the auto-navigation (systems/nav.ts) can walk to by name, and the phrases of quest text that name them
 * (the quest tracker makes them links). A place is a spot on a zone's map: a landmark of a hand-laid layout, the
 * middle of a hunting region, the lair, the plaza, or the elder. Data only: add a phrase or a place here.
 */
import type { RegionKey } from '../map';

/** Where a place is on the map it is looked up on (the first of these the map has). */
export type NavSpot =
  | { readonly landmark: string }
  | { readonly region: RegionKey }
  | { readonly lair: true }
  | { readonly arena: true }
  | { readonly plaza: true }
  | { readonly elder: true };

export interface NavPlace {
  readonly id: string;
  /** Its name, said in the notices ("นำทางไป…"). */
  readonly name: string;
  /** Phrases of quest text that name it, longest matched first. */
  readonly phrases: readonly string[];
  /** Where it is: tried in order on the zone's map. */
  readonly at: readonly NavSpot[];
}

/** The places quests send the player to (data/quests.ts `offer` / `remind`). */
const QUEST_PLACES: readonly NavPlace[] = [
  { id: 'elder', name: 'ผู้เฒ่าหลิว', phrases: ['ผู้เฒ่าหลิว', 'ศิลาเก้ากระบี่'], at: [{ elder: true }] },
  { id: 'yard', name: 'ลานฝึก', phrases: ['ลานฝึก'], at: [{ landmark: 'trainYard' }, { region: 'west' }] },
  { id: 'innDoor', name: 'โรงเตี๊ยม', phrases: ['โรงเตี๊ยม'], at: [{ landmark: 'inn' }, { plaza: true }] },
  { id: 'pines', name: 'ป่าสน', phrases: ['ป่าต้นน้ำทางเหนือ', 'ป่าต้นน้ำ', 'ป่าสนทางตะวันออก', 'ป่าสน'], at: [{ region: 'wolves' }] },
  {
    id: 'banditHill',
    name: 'เนินโจรภูเขา',
    phrases: ['เนินโจรทางใต้ของลาน', 'เนินเขาทางตะวันตกเฉียงใต้', 'ประตูนา'],
    at: [{ region: 'bandits' }],
  },
  { id: 'bloodGround', name: 'ลานเลือด', phrases: ['ลานเลือดบนผาแดง', 'ลานเลือด'], at: [{ lair: true }] },
  { id: 'demonGround', name: 'ลานจอมมาร', phrases: ['ลานจอมมาร'], at: [{ arena: true }] },
];

/** Yanyu's named spots (landmarks of its layout, named as the story names them). */
const SPOTS: readonly (readonly [id: string, name: string])[] = [
  ['pier', 'สะพานท่าเรือ'],
  ['pavilion', 'ศาลากลางน้ำ'],
  ['shrine', 'ลานศาลเจ้า'],
  ['elderHouse', 'บ้านผู้เฒ่าหลิว'],
  ['shop', 'ร้านค้าหยานหยู'],
  ['schools', 'ศาลาหกสำนัก'],
  ['pond', 'สระใต้น้ำตก'],
  ['gate', 'ซุ้มประตูหมู่บ้าน'],
  ['pass', 'ช่องเขาตะวันตก'],
];

/** Every place: the quests' own, then Yanyu's named spots. */
export const NAV_PLACES: readonly NavPlace[] = [
  ...QUEST_PLACES,
  ...SPOTS.map(([id, name]): NavPlace => ({ id, name, phrases: [name], at: [{ landmark: id }] })),
];

export const navPlace = (id: string): NavPlace | undefined => NAV_PLACES.find((p) => p.id === id);

/** Phrases to places, longest first (so "ป่าสนทางตะวันออก" wins over "ป่าสน"). */
const PHRASES: readonly (readonly [string, string])[] = NAV_PLACES.flatMap((p) =>
  p.phrases.map((s) => [s, p.id] as const),
).sort((a, b) => b[0].length - a[0].length);

/** A piece of quest text: plain, or a phrase naming a place (`place`: its id). */
export interface NavTextPart {
  readonly text: string;
  readonly place?: string;
}

/** Quest text cut into plain pieces and the phrases that name places, in order (the tracker's links). */
export function navLinks(text: string): NavTextPart[] {
  const out: NavTextPart[] = [];
  let plain = '';
  let k = 0;
  while (k < text.length) {
    const hit = PHRASES.find(([s]) => text.startsWith(s, k));
    if (hit) {
      if (plain) out.push({ text: plain });
      plain = '';
      out.push({ text: hit[0], place: hit[1] });
      k += hit[0].length;
    } else plain += text[k++];
  }
  if (plain) out.push({ text: plain });
  return out;
}
